export const AUTH_RECOVERY_TIMEOUT_MS = 12000;
export const RESET_SESSION_SIGN_OUT_TIMEOUT_MS = 5000;

export const AUTH_RECOVERY_TITLE =
  "We're having trouble restoring your session in this browser.";
export const AUTH_RECOVERY_HELP_TEXT =
  'This can happen if local site data, storage, or the network gets stuck. You can reload, reset the local session, or clear website data for affinityai.app if the problem continues.';
export const AUTH_RECOVERY_FOOTER_TEXT =
  'Reset only clears local AffinityAI session data on this device.';
export const ACCESS_RECOVERY_TITLE = "We're having trouble checking your access.";
export const ACCESS_RECOVERY_HELP_TEXT =
  'Try reloading. If this keeps happening, reset the local session and sign in again.';
export const ACCESS_RECOVERY_FOOTER_TEXT = 'Your access has not been changed.';

const AUTH_TIMEOUT_CODE = 'auth-recovery/timeout';

const APP_OWNED_STORAGE_PREFIXES = [
  'aa.notify.dismissed.autoClosed.',
  'affinity.',
  'affinityai:',
  'affinity.notificationsDismissed:',
  'dashboardReadyBannerShown:',
  'hdr-lines:',
  'onboarding.emptyDashboard.wasEmpty',
  'onboarding.firstProject.newProjectId:',
  'pc-open-',
  'pc-slides-open-',
  'selected-',
  'setupDraft:v1:',
  'shorten:',
  'summary-board-deck-mode:',
  'team-member-intro-video-dismissed:',
];

const SENSITIVE_METADATA_RE = /password|token|secret|answer|prompt|content|output/i;

function createTimeoutError(operation) {
  const error = new Error(`${operation || 'operation'} timed out`);
  error.code = AUTH_TIMEOUT_CODE;
  error.operation = operation || 'operation';
  return error;
}

export function isAuthRecoveryTimeout(error) {
  return error?.code === AUTH_TIMEOUT_CODE;
}

export function withAuthRecoveryTimeout(
  promise,
  operation,
  timeoutMs = AUTH_RECOVERY_TIMEOUT_MS,
) {
  let timeoutId;
  let settled = false;

  return new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(createTimeoutError(operation));
    }, timeoutMs);

    Promise.resolve(promise).then(
      (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

export function getSafeFirebaseErrorCode(error) {
  return typeof error?.code === 'string' && error.code ? error.code : 'unknown';
}

export function getSafeErrorCategory(error) {
  const code = getSafeFirebaseErrorCode(error);
  if (isAuthRecoveryTimeout(error)) return 'timeout';
  if (code.startsWith('auth/')) return 'firebase_auth';
  if (code.includes('permission-denied')) return 'permission';
  if (code.includes('unavailable') || code.includes('network')) return 'network';
  return 'unknown';
}

function sanitizeDiagnosticMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return {};
  return Object.entries(metadata).reduce((acc, [key, value]) => {
    if (SENSITIVE_METADATA_RE.test(key)) return acc;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === null
    ) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function logAuthDiagnostic(event, metadata = {}) {
  if (typeof console === 'undefined' || typeof console.info !== 'function') return;
  console.info('[auth.recovery]', {
    event,
    ...sanitizeDiagnosticMetadata(metadata),
  });
}

function isAppOwnedStorageKey(key) {
  return APP_OWNED_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function getWindowStorage(kind, win = typeof window !== 'undefined' ? window : null) {
  if (!win) return null;
  try {
    return kind === 'local' ? win.localStorage : win.sessionStorage;
  } catch (error) {
    logAuthDiagnostic(`storage.${kind}.access.failure`, {
      code: getSafeFirebaseErrorCode(error),
      category: getSafeErrorCategory(error),
    });
    return null;
  }
}

function safeGetStorageItem(kind, key, fallback = null, source = 'storage') {
  const storage = getWindowStorage(kind);
  if (!storage) return fallback;
  try {
    const value = storage.getItem(key);
    return value === null ? fallback : value;
  } catch (error) {
    logAuthDiagnostic(`storage.${kind}.get.failure`, {
      source,
      code: getSafeFirebaseErrorCode(error),
      category: getSafeErrorCategory(error),
    });
    return fallback;
  }
}

function safeSetStorageItem(kind, key, value, source = 'storage') {
  const storage = getWindowStorage(kind);
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    logAuthDiagnostic(`storage.${kind}.set.failure`, {
      source,
      code: getSafeFirebaseErrorCode(error),
      category: getSafeErrorCategory(error),
    });
    return false;
  }
}

function safeRemoveStorageItem(kind, key, source = 'storage') {
  const storage = getWindowStorage(kind);
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch (error) {
    logAuthDiagnostic(`storage.${kind}.remove.failure`, {
      source,
      code: getSafeFirebaseErrorCode(error),
      category: getSafeErrorCategory(error),
    });
    return false;
  }
}

export function safeGetSessionStorageItem(key, fallback = null, source = 'storage') {
  return safeGetStorageItem('session', key, fallback, source);
}

export function safeSetSessionStorageItem(key, value, source = 'storage') {
  return safeSetStorageItem('session', key, value, source);
}

export function safeRemoveSessionStorageItem(key, source = 'storage') {
  return safeRemoveStorageItem('session', key, source);
}

export function safeGetLocalStorageItem(key, fallback = null, source = 'storage') {
  return safeGetStorageItem('local', key, fallback, source);
}

export function safeSetLocalStorageItem(key, value, source = 'storage') {
  return safeSetStorageItem('local', key, value, source);
}

export function safeRemoveLocalStorageItem(key, source = 'storage') {
  return safeRemoveStorageItem('local', key, source);
}

function clearMatchingStorage(storage) {
  const removedKeys = [];
  if (!storage) return removedKeys;

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (!key || !isAppOwnedStorageKey(key)) continue;
    try {
      storage.removeItem(key);
      removedKeys.push(key);
    } catch (error) {
      logAuthDiagnostic('storage.clear.failure', {
        code: getSafeFirebaseErrorCode(error),
        category: getSafeErrorCategory(error),
      });
    }
  }

  return removedKeys;
}

export function clearAppOwnedLocalSessionStorage(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return [];
  const removedKeys = [];

  try {
    removedKeys.push(...clearMatchingStorage(win.localStorage));
  } catch (error) {
    logAuthDiagnostic('storage.local.clear.failure', {
      code: getSafeFirebaseErrorCode(error),
      category: getSafeErrorCategory(error),
    });
  }

  try {
    removedKeys.push(...clearMatchingStorage(win.sessionStorage));
  } catch (error) {
    logAuthDiagnostic('storage.session.clear.failure', {
      code: getSafeFirebaseErrorCode(error),
      category: getSafeErrorCategory(error),
    });
  }

  return removedKeys;
}

export async function resetLocalAuthSession({
  auth,
  signOutFn,
  timeoutMs = RESET_SESSION_SIGN_OUT_TIMEOUT_MS,
} = {}) {
  logAuthDiagnostic('reset_local_session.start');
  await withAuthRecoveryTimeout(signOutFn(auth), 'reset_local_session.sign_out', timeoutMs);
  const removedKeys = clearAppOwnedLocalSessionStorage();
  logAuthDiagnostic('reset_local_session.success', {
    removedStorageKeys: removedKeys.length,
  });
  return { removedKeys };
}
